import ReactGA from 'react-ga';
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { isWebComponentFlow } from './commonCode';
import { wcApiUrl } from '../WebComponents/commonComponents/customUtils';

const Analytics = (props) => {
  const baseContext = isWebComponentFlow();
  const { initiateUiResponse, setGoogleCID, environment, wcFlow } = baseContext;
  const { analyticsInitialized } = props;

  const gaInitializedRef = useRef(false);
  const gtmInitializedRef = useRef(false);
  const gaReadyRef = useRef(false);
  const isCancelledRef = useRef(false);

  const getGATrackingID = () =>
    wcFlow
      ? wcApiUrl(environment).REACT_APP_GA_TRACKING_ID[environment]
      : initiateUiResponse?.REACT_APP_GA_TRACKING_ID;

  const getGTMTrackingID = () =>
    wcFlow
      ? wcApiUrl(environment).REACT_APP_GTM_ID[environment]
      : initiateUiResponse?.REACT_APP_GTM_ID;

  const envCondition = wcFlow
    ? environment === 'dev' || environment === 'prod'
    : initiateUiResponse?.REACT_APP_ENVIRONMENT === 'production' ||
      initiateUiResponse?.REACT_APP_ENVIRONMENT === 'development';

  /*
   * ============================================================
   * MINIMAL ANALYTICS DEBUG
   * ============================================================
   */

  const logAnalyticsState = (stage) => {
    console.log(`[Analytics][${stage}]`, {
      wcFlow,
      ourGA: getGATrackingID(),
      ourGTM: getGTMTrackingID(),
      gtag: typeof window.gtag,
      ga: typeof window.ga,
      gaGetAll: typeof window.ga?.getAll,
      dataLayerLength: window.dataLayer?.length,
    });
  };

  /*
   * ============================================================
   * COOKIE CID
   *
   * We only inspect the standard _ga cookie.
   *
   * _ga_xxxxx cookies are GA4 property-specific cookies and
   * should NOT be treated as the generic Client ID.
   * ============================================================
   */

  /*
   * ============================================================
   * GA INITIALIZATION
   * ============================================================
   */

  const initGA = (id) => {
    if (!id || gaInitializedRef.current) return;

    console.log('[Analytics][GA INIT]', {
      trackingId: id,
      existingGtag: typeof window.gtag,
      existingGa: typeof window.ga,
      existingGaGetAll: typeof window.ga?.getAll,
      existingDataLayerLength: window.dataLayer?.length,
    });

    gaInitializedRef.current = true;

    window.dataLayer = window.dataLayer || [];

    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    }

    /*
     * Preserve existing behavior:
     * WC owns gtag for its initialization.
     */
    window.gtag = gtag;

    const script = document.createElement('script');

    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;

    script.onload = () => {
      console.log('[Analytics][GA SCRIPT LOADED]', {
        trackingId: id,
        gtag: typeof window.gtag,
        ga: typeof window.ga,
        gaGetAll: typeof window.ga?.getAll,
      });

      window.gtag('js', new Date());
      window.gtag('config', id);

      gaReadyRef.current = true;

      /*
       * One concise dataLayer verification.
       */
      console.log('[Analytics][GA CONFIG SENT]', {
        trackingId: id,
        dataLayerLength: window.dataLayer?.length,
      });
    };

    script.onerror = () => {
      console.error('[Analytics][GA SCRIPT FAILED]', script.src);

      gaInitializedRef.current = false;
      gaReadyRef.current = false;
    };

    document.head.appendChild(script);
  };

  /*
   * ============================================================
   * GTM INITIALIZATION
   * ============================================================
   */

  const initGTM = ({ gtmId, dataLayerName = 'dataLayer' }) => {
    if (!gtmId || gtmInitializedRef.current) {
      return;
    }

    gtmInitializedRef.current = true;

    window[dataLayerName] = window[dataLayerName] || [];

    window[dataLayerName].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const script = document.createElement('script');

    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}${
      dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : ''
    }`;

    script.onerror = () => {
      gtmInitializedRef.current = false;
    };

    document.head.appendChild(script);
  };

  /*
   * ============================================================
   * CID CAPTURE
   *
   * WC:
   *   1. gtag('get')
   *   2. 30 attempts / 100ms
   *   3. _ga cookie fallback
   *
   * DS:
   *   Existing window.ga.getAll() behavior
   * ============================================================
   */

  const captureCID = () => {
    /*
     * ============================================================
     * WEB COMPONENT FLOW
     *
     * 1. Try gtag('get', ..., 'client_id')
     * 2. Retry for 30 attempts
     * 3. If gtag does not return CID, try _ga cookie
     *
     * DS flow below remains exactly as it was.
     * ============================================================
     */
    if (wcFlow) {
      const maxAttempts = 30;
      let attempts = 0;
      let cidFound = false;

      const getCIDFromCookie = () => {
        const gaCookie = document.cookie.split('; ').find((cookie) => cookie.startsWith('_ga='));

        if (!gaCookie) {
          console.warn('[Analytics][WC CID] _ga cookie not found');
          return null;
        }

        const cookieValue = decodeURIComponent(gaCookie.substring('_ga='.length));

        const parts = cookieValue.split('.');

        if (parts.length < 4) {
          console.warn('[Analytics][WC CID] Invalid _ga cookie');
          return null;
        }

        const clientId = `${parts[2]}.${parts[3]}`;

        console.log('[Analytics][WC CID][COOKIE] SUCCESS', {
          clientId,
        });

        return clientId;
      };

      const fallbackToCookie = () => {
        if (isCancelledRef.current || cidFound) return;

        console.warn('[Analytics][WC CID] GTAG failed - trying _ga cookie');

        const cookieCID = getCIDFromCookie();

        if (cookieCID) {
          cidFound = true;
          setGoogleCID(cookieCID);
        } else {
          console.error('[Analytics][WC CID] FAILED - no CID from gtag or _ga cookie');
        }
      };

      const checkGTAG = () => {
        if (isCancelledRef.current || cidFound) return;

        attempts += 1;

        const trackingId = getGATrackingID();

        if (typeof window.gtag !== 'function' || !trackingId) {
          if (attempts === 1) {
            console.warn('[Analytics][WC CID] gtag unavailable');
          }

          if (attempts < maxAttempts) {
            setTimeout(checkGTAG, 100);
          } else {
            fallbackToCookie();
          }

          return;
        }

        try {
          window.gtag('get', trackingId, 'client_id', (clientId) => {
            if (isCancelledRef.current || cidFound) return;

            if (clientId) {
              cidFound = true;

              console.log('[Analytics][WC CID][GTAG] SUCCESS', {
                attempt: attempts,
                clientId,
              });

              setGoogleCID(clientId);
            }
          });
        } catch (error) {
          if (attempts === 1) {
            console.error('[Analytics][WC CID] gtag get error', error);
          }
        }

        /*
         * IMPORTANT:
         *
         * Retry is NOT dependent on the gtag callback.
         *
         * If gtag never invokes the callback, we still continue
         * retrying and eventually fall back to the cookie.
         */
        if (!cidFound) {
          if (attempts < maxAttempts) {
            setTimeout(checkGTAG, 100);
          } else {
            fallbackToCookie();
          }
        }
      };

      console.log('[Analytics][WC CID] START', {
        trackingId: getGATrackingID(),
        gtag: typeof window.gtag,
      });

      checkGTAG();

      return;
    }

    /*
     * ============================================================
     * DS FLOW
     *
     * DO NOT CHANGE THIS LOGIC.
     * ============================================================
     */
    let attempts = 0;
    const maxAttempts = 30;

    const checkCID = () => {
      if (isCancelledRef.current) return;

      attempts += 1;

      if (window?.ga?.getAll && !wcFlow) {
        const trackers = window.ga.getAll();
        const tracker = trackers.find((t) => t.get('trackingId') === getGATrackingID());

        if (tracker) {
          const clientId = tracker.get('clientId');

          if (clientId) {
            setGoogleCID(clientId);
            return;
          }
        }
      }

      if (attempts < maxAttempts) {
        setTimeout(checkCID, 100);
      }
    };

    checkCID();
  };

  /*
   * ============================================================
   * EFFECT
   * ============================================================
   */

  useEffect(() => {
    isCancelledRef.current = false;

    console.log('========== [Analytics] START ==========');

    logAnalyticsState('BEFORE INIT');

    /*
     * GA INITIALIZATION
     */

    if (getGATrackingID() && !analyticsInitialized) {
      initGA(getGATrackingID());
    }

    /*
     * GTM + ReactGA
     */

    if (envCondition && !analyticsInitialized) {
      // Replaced react-gtm-module to avoid its inline GTM bootstrap script, which violates CSP.
      initGTM({
        gtmId: getGTMTrackingID(),
      });

      const tryInitReactGA = () => {
        if (isCancelledRef.current) return;

        if (!gaReadyRef.current) {
          setTimeout(tryInitReactGA, 50);
          return;
        }

        /*
         * Important diagnostic:
         *
         * We want to know whether ReactGA.initialize changes
         * window.ga/getAll after our GA initialization.
         */
        const beforeReactGA = {
          ga: typeof window.ga,
          gaGetAll: typeof window.ga?.getAll,
        };

        ReactGA.initialize(getGATrackingID());

        const afterReactGA = {
          ga: typeof window.ga,
          gaGetAll: typeof window.ga?.getAll,
        };

        console.log('[Analytics][ReactGA]', {
          before: beforeReactGA,
          after: afterReactGA,
        });

        ReactGA.pageview(window.location.pathname + window.location.search);
      };

      tryInitReactGA();
    }

    /*
     * Existing analytics already initialized elsewhere.
     */

    if (analyticsInitialized) {
      gaReadyRef.current = true;
    }

    /*
     * CID
     */

    const tryCaptureCID = () => {
      if (isCancelledRef.current) return;

      if (!gaReadyRef.current) {
        setTimeout(tryCaptureCID, 50);
        return;
      }

      captureCID();
    };

    tryCaptureCID();

    /*
     * One final snapshot after initialization.
     *
     * This is useful for comparing:
     *
     * BEFORE INIT
     * vs
     * AFTER INIT
     */
    const finalStateTimeout = setTimeout(() => {
      if (isCancelledRef.current) return;

      logAnalyticsState('FINAL');
    }, 3000);

    return () => {
      clearTimeout(finalStateTimeout);
      isCancelledRef.current = true;
    };
  }, [initiateUiResponse]);

  return null;
};

export default Analytics;

Analytics.propTypes = {
  analyticsInitialized: PropTypes.bool,
};
